/**
 * Configuración de motivos para informes
 * Unificación de motivos, campos, y templates
 */

/**
 * Formatea una fecha YYYY-MM-DD a formato legible DD/MM/YYYY
 * Evita problemas de timezone al no usar new Date() con string
 */
const formatearFecha = (fechaString) => {
  if (!fechaString) return '';
  // Si ya viene en formato DD/MM/YYYY, devolverla tal cual
  if (fechaString.includes('/')) return fechaString;
  // Convertir YYYY-MM-DD a DD/MM/YYYY manualmente (sin timezone)
  const [year, month, day] = fechaString.split('-');
  return `${day}/${month}/${year}`;
};

export const motivosConfig = {
  AbandonoServicio: {
    key: 'AbandonoServicio',
    label: 'Abandono de Servicio',
    fields: ['horaPresentacion', 'lugarAbandono'],
    template: (data) =>
      `Informo que el conductor mencionado ingresa a trabajar a las ${data.horaPresentacion || ''} hs. y corta el servicio en ${data.lugarControl || ''} a las ${data.hora || ''} hs haciendo abandono de servicio, dejando la unidad en ${data.lugarAbandono || ''}.`,
    allowManualMessage: false
  },

  AbandonoTemporal: {
    key: 'AbandonoTemporal',
    label: 'Abandono Temporal del Lugar de Trabajo',
    fields: ['horaPresentacion', 'horaFin', 'horaInicioAbandono', 'horaFinAbandono'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} mientras cumple con su servicio en el horario desde ${data.horaPresentacion || ''} hs hasta las ${data.horaFin || ''}, hace abandono de su lugar trabajo sin previo aviso ni justificativo en el horario comprendido entre las ${data.horaInicioAbandono || ''} hs y las ${data.horaFinAbandono || ''} hs .`,
    allowManualMessage: false
  },

  Adelanto: {
    key: 'Adelanto',
    label: 'Adelanto',
    fields: ['horarioPasoTeorico'],
    template: (data) => {
      let minutosAdelanto = '';
      if (data.horarioPasoTeorico && data.hora) {
        const [hTeor, mTeor] = data.horarioPasoTeorico.split(':').map(Number);
        const [hReal, mReal] = data.hora.split(':').map(Number);
        const minutosTeor = hTeor * 60 + mTeor;
        const minutosReal = hReal * 60 + mReal;
        minutosAdelanto = minutosTeor - minutosReal;
      }
      return `Informo que el conductor antes mencionado a cargo de la unidad citada que debía llegar al lugar de control a la hora ${data.horarioPasoTeorico || ''}, lo hace a la hora ${data.hora || ''} con ${minutosAdelanto || ''} minutos de adelanto injustificado`;
    },
    allowManualMessage: false
  },

  AtrasInjustificado: {
    key: 'AtrasInjustificado',
    label: 'Atraso Injustificado',
    fields: ['horarioPasoTeorico'],
    template: (data) => {
      let minutosAtraso = '';
      if (data.horarioPasoTeorico && data.hora) {
        const [hTeor, mTeor] = data.horarioPasoTeorico.split(':').map(Number);
        const [hReal, mReal] = data.hora.split(':').map(Number);
        const minutosTeor = hTeor * 60 + mTeor;
        const minutosReal = hReal * 60 + mReal;
        minutosAtraso = minutosReal - minutosTeor;
      }
      return `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, por ${data.lugarControl || ''} con ${minutosAtraso || ''} minutos de atraso injustificado.`;
    },
    allowManualMessage: false
  },

  Ausencia: {
    key: 'Ausencia',
    label: 'Ausencia',
    fields: ['horaPresentacion', 'servicio'],
    template: (data) =>
      `Explique los motivos por los cuales no se presenta a tomar servicio el día ${formatearFecha(data.fecha)} a la hora ${data.horaPresentacion || ''} debiendo realizar el servicio ${data.servicio || ''} por linea ${data.linea || ''} con horario de presentacion ${data.horaPresentacion || ''} hs.`,
    allowManualMessage: false
  },

  DejaInspectorParada: {
    key: 'DejaInspectorParada',
    label: 'Deja Inspector en la Parada',
    fields: ['alturaCalle'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, no detiene su marcha ante la presencia de pasajeros e Inspector en la parada de calle ${data.lugarControl || ''} al N° ${data.alturaCalle || ''}, perjudicando económicamente a la empresa (y evadiendo de esta manera el control correspondiente).`,
    allowManualMessage: false
  },

  DejaPasajerosInspectorParada: {
    key: 'DejaPasajerosInspectorParada',
    label: 'Deja Pasajero/s (e Inspector) en la Parada',
    fields: ['alturaCalle'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, no detiene su marcha ante la presencia del Inspector en la parada de calle ${data.lugarControl || ''} al N° ${data.alturaCalle || ''} , evadiendo de esta manera el control correspondiente.`,
    allowManualMessage: false
  },

  FueraRecorrido: {
    key: 'FueraRecorrido',
    label: 'Fuera de Recorrido',
    fields: ['rutaRecorrido', 'rutaFueraRecorrido'],
    template: (data) =>
      `Comunico al Sr. Jefe de Tráfico que el conductor antes mencionado a cargo de la unidad N° ${data.nroCoche || ''} el día de la fecha y a la hora indicada no realiza el recorrido establecido a saber: ${data.rutaRecorrido || ''}, haciéndolo en cambio por ${data.rutaFueraRecorrido || ''}, sin autorización alguna.`,
    allowManualMessage: false
  },

  LlegadaTarde: {
    key: 'LlegadaTarde',
    label: 'Llegada Tarde',
    fields: ['horaPresentacion', 'lugarPresentacion', 'horaPresentacionReal'],
    template: (data) =>
      `Informo que el conductor antes mencionado debía presentarse a trabajar a las ${data.horaPresentacion || ''} Hs en ${data.lugarPresentacion || ''}, y se presenta a las ${data.horaPresentacionReal || ''} en ${data.lugarControl || ''}.- Tomando servicio en Línea ${data.linea || ''} A partir de las ${data.horaPresentacionReal || ''} Hs en la unidad N° ${data.nroCoche || ''} -`,
    allowManualMessage: false
  },

  NoLogueaGPS: {
    key: 'NoLogueaGPS',
    label: 'No se Loguea (no ingresa datos al GPS)',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, lo hace sin haberse logueado, es decir sin ingresar los datos correspondientes al G.P.S. de la unidad.`,
    allowManualMessage: false
  },

  VelocidadExcesiva: {
    key: 'VelocidadExcesiva',
    label: 'Velocidad Excesiva',
    fields: ['velocidad'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, por ${data.lugarControl || ''} a velocidad mayor a la permitida para dicha arteria: ${data.velocidad || ''} Km/h., poniendo en riesgo su integridad física, la de terceros y la unidad a su cargo.`,
    allowManualMessage: false
  },

  CorteInjustificado: {
    key: 'CorteInjustificado',
    label: 'Corte Injustificado de Servicio',
    fields: ['fallaMecanica'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras presta servicio a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, interrumpe el recorrido manifestando tener los siguientes inconvenientes mecánicos: ${data.fallaMecanica || ''}, el/los cuales al ser revisado/s por personal de técnica no justifican el corte de servicio, perjudicando con su accionar al servicio en frecuencia, regularidad y económicamente a la empresa.`,
    allowManualMessage: false
  },

  DesconectaAntenaMicronauta: {
    key: 'DesconectaAntenaMicronauta',
    label: 'Desconecta Antena GPS Micronauta',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted desconecta la antena de G.P.S. de la unidad.`,
    allowManualMessage: false
  },

  DesconectaMicronauta: {
    key: 'DesconectaMicronauta',
    label: 'Desconecta GPS Micronauta',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted la consola Micronauta de G.P.S. de la unidad.`,
    allowManualMessage: false
  },

  ConduccionPuertaAbierta: {
    key: 'ConduccionPuertaAbierta',
    label: 'Conduce con la/s Puerta/s Abierta/s',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted lo hace con la/s puerta/s abierta/s. Provocando una situación de grave peligro para el pasaje.`,
    allowManualMessage: false
  },

  ConduccionFumando: {
    key: 'ConduccionFumando',
    label: 'Conduce Fumando',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted lo hace fumando`,
    allowManualMessage: false
  },

  ConduccionConCelular: {
    key: 'ConduccionConCelular',
    label: 'Conduce Utilizando Teléfono Celular',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted lo utilizando telefono celular violando la normativa vigente.`,
    allowManualMessage: false
  },

  ConduccionConAuriculares: {
    key: 'ConduccionConAuriculares',
    label: 'Conduce Utilizando Auriculares',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs mientras circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, usted lo utilizando auriculares violando la normativa vigente.`,
    allowManualMessage: false
  },

  DañoUnidad: {
    key: 'DañoUnidad',
    label: 'Daño a la Unidad',
    fields: ['fallaMecanica'],
    template: (data) =>
      `Explique las circunstancian en las que el día ${formatearFecha(data.fecha)} En las que la unidad interno N° ${data.nroCoche || ''} a su cargo termina con los siguientes daños: ${data.fallaMecanica || ''}`,
    allowManualMessage: false
  },

  NoControl: {
    key: 'NoControl',
    label: 'No Cumple Procedimiento de Ausencia (Control Médico)',
    fields: ['horaPresentacion', 'horaRetiro'],
    template: (data) =>
      `Informo que el conductor mencionado ingresa a trabajar a las ${data.horaPresentacion || ''} hs. y se retira a las ${data.horaRetiro || ''} hs no cumpliendo el ciclo de horas de la jornada laboral.-`,
    allowManualMessage: false
  },

  NoCertificado: {
    key: 'NoCertificado',
    label: 'No Cumple Procedimiento de Ausencias (no confirma certificado ni concurre a control)',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales comunica ausencia por enfermedad el día ${formatearFecha(data.fecha)}, confirma certificado y no se presenta al control médico laboral.-`,
    allowManualMessage: false
  },

  NoAvisa: {
    key: 'NoAvisa',
    label: 'No Cumple Procedimiento de Ausencias (no avisa, presenta documentación)',
    fields: [],
    template: (data) =>
      `Explique los motivos por los cuales comunica ausencia por enfermedad el día ${formatearFecha(data.fecha)} , no confirma certificado y no se presenta al control médico laboral..-`,
    allowManualMessage: false
  },

  AusenteDomicilio: {
    key: 'AusenteDomicilio',
    label: 'Ausente en domicilio ante control médico',
    fields: ['servicio'],
    template: (data) =>
      `Comunico al Sr. Jefe de Tráfico que el conductor antes mencionado no se presentó el día de la fecha a cubrir el servicio N° ${data.servicio || ''} de línea ${data.linea || ''} para el cual estaba diagramado.`,
    allowManualMessage: false
  },

  AusenciaFueraTermino: {
    key: 'AusenciaFueraTermino',
    label: 'Aviso de Ausencias fuera de término',
    fields: ['fechaCM'],
    template: (data) =>
      `Explique los motivos por los cuales comunica ausencia por enfermedad el día ${formatearFecha(data.fecha)}, encontrándose ausente en su domicilio al momento del control médico realizado el día ${formatearFecha(data.fechaCM)} a las ${data.hora || ''} hs.`,
    allowManualMessage: false
  },

  SiniestroFueraTermino: {
    key: 'SiniestroFueraTermino',
    label: 'Denuncia de Siniestro Fuera de Término',
    fields: ['alturaCalle'],
    template: (data) =>
      `Explique los motivos por los cuales el día ${formatearFecha(data.fecha)} siendo las ${data.hora || ''} hs circula a cargo de la unidad interno N° ${data.nroCoche || ''} por línea ${data.linea || ''}, no detiene su marcha ante la presencia de pasajeros e Inspector en la parada de calle ${data.lugarControl || ''} al N° ${data.alturaCalle || ''} ,perjudicando económicamente a la empresa (y evadiendo de esta manera el control correspondiente).`,
    allowManualMessage: false
  },

  SiniestroVial: {
    key: 'SiniestroVial',
    label: 'Siniestro Vial',
    fields: ['fechaDenuncia', 'fechaSiniestro', 'horaSiniestro', 'lugarSiniestro', 'marcaVehiculo', 'dominioVehiculo', 'msgRoturas'],
    template: (data) =>
      `Según lo especifica la denuncia formulada el día ${formatearFecha(data.fechaDenuncia)}, tuvo un siniestros el día ${formatearFecha(data.fechaSiniestro)} a las ${data.horaSiniestro || ''} hs. en ${data.lugarSiniestro || ''} -lugar del accidente-. Con el vehículo marca ${data.marcaVehiculo || ''} Dominio ${data.dominioVehiculo || ''} , declarando las siguientes roturas ${data.msgRoturas || ''} , se le solicita amplíe la información con mayores datos a los fines que pueda dar una mejor precisión de cómo sucedieron los hechos.`,
    allowManualMessage: false
  },

  MalDesempeñoTareas: {
    key: 'MalDesempeñoTareas',
    label: 'Mal Desempeño de sus Tareas',
    fields: [],
    template: (data) => '',
    allowManualMessage: true
  },

  UniformeIncompleto: {
    key: 'UniformeIncompleto',
    label: 'Uniforme Incompleto',
    fields: ['servicio', 'detalleVestimenta'],
    template: (data) =>
      `Explique el motivo por el cual en el dia de la fecha realizando servicio ${data.servicio || ''}. Usted incurre en la siguiente falta en el codigo de vestimenta: ${data.detalleVestimenta || ''}.`,
    allowManualMessage: false
  },

  Infraccion: {
    key: 'Infraccion',
    label: 'Infracción',
    fields: ['nroInfraccion', 'subMotivosInfraccion'],
    template: (data) => {
      const conductas = Array.isArray(data.subMotivosInfraccion) && data.subMotivosInfraccion.length > 0
        ? data.subMotivosInfraccion.join(', ')
        : '';
      return `Se deja constancia de la Infracción N° ${data.nroInfraccion || ''}, habiéndose constatado la/s siguiente/s conducta/s: ${conductas}.

Por medio de la presente se deja constancia que, conforme a lo observado y registrado por el Observatorio de la Movilidad de la Municipalidad de Córdoba, mientras Ud. se encontraba a cargo de la unidad, se habría constatado la conducta ya detallada.
Dicha conducta podría resultar contraria a las disposiciones de la Ley Nacional de Tránsito N.º 24.449, la Ley Provincial de Tránsito N.º 8560, sus respectivas normas reglamentarias y modificatorias, y la Ordenanza Municipal de Tránsito N.º 9981/98, sus modificatorias y demás normativa municipal vigente.
En su carácter de conductor profesional de transporte público de pasajeros, le corresponde observar un especial deber de prudencia, diligencia, responsabilidad y estricto cumplimiento de las normas de tránsito y seguridad vial, teniendo especialmente en consideración la responsabilidad que implica transportar pasajeros y preservar la integridad de todos los usuarios de la vía pública.
En virtud de lo expuesto, se solicita que efectúe por escrito su descargo respecto de la conducta observada, indicando los motivos y circunstancias que considere pertinentes, a fin de que sean evaluados conforme a la normativa vigente y a las disposiciones internas de la Empresa.`;
    },
    allowManualMessage: false
  },

  Otros: {
    key: 'Otros',
    label: 'Otros',
    fields: [],
    template: () => '',
    allowManualMessage: true
  },

  NoCumpleTareasHorario: {
    key: 'NoCumpleTareasHorario',
    label: 'No Realiza o Cumple Tareas en Horario Laboral',
    fields: [],
    template: () => '',
    allowManualMessage: true
  },

  Generales: {
    key: 'Generales',
    label: 'Generales',
    fields: [],
    template: () => '',
    allowManualMessage: true,
    // Usa el header F073 (Informes Disciplinarios): sin sección de descargo,
    // solo el texto que carga quien realiza el informe (informes más detallados).
    pdfTemplate: 'F073'
  }
};

/**
 * Obtener array de motivos para el select
 */
export const getMotivosOptions = () => {
  return Object.values(motivosConfig).map(motivo => ({
    value: motivo.key,
    label: motivo.label
  }));
};

/**
 * Obtener configuración de un motivo por su key
 */
export const getMotivoConfig = (key) => {
  return motivosConfig[key] || null;
};

/**
 * Generar mensaje para un motivo
 */
export const generarMensajeMotivo = (motivoKey, datos) => {
  const config = getMotivoConfig(motivoKey);
  if (!config) {
    return '';
  }
  
  try {
    return config.template(datos);
  } catch (error) {
    console.error(`Error generando mensaje para ${motivoKey}:`, error);
    return '';
  }
};

